import { BarChart, LineChart } from "@mui/x-charts";
import { Box, Typography } from "@mui/material";
import * as arrow from "apache-arrow";
import React, { useMemo } from "react";

import { ChartConfig } from "../../entities/chart_config";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { getChartDataFromArrowTable } from "../../utils/mui_chart_data";
import datejs from "../../utils/dayjs";

export type ChartType = "bar" | "line";

export interface ChartDataset {
  id: string;
  label: string;
  color: string;
  data: number[];
}

export interface UniversalChartViewerProps<
  T extends DatePivotParams = DatePivotParams,
> {
  parsedData: {
    arrowTable: arrow.Table;
    columns: arrow.Field<any>[];
  };
  viewParams: T;
  chartConfig: ChartConfig;
}

export const UniversalChartViewer = <
  T extends DatePivotParams = DatePivotParams,
>(
  props: UniversalChartViewerProps<T>
) => {
  const { parsedData, chartConfig } = props;

  // Process Arrow table data for charts
  const chartData = getChartDataFromArrowTable({
    arrowTable: parsedData.arrowTable,
    columns: parsedData.columns,
    xAxisField: chartConfig.xAxisField,
    yAxisFields: chartConfig.yAxisFields,
    datasetLabels: chartConfig.datasetLabels || {},
    datasetColors: chartConfig.datasetColors || {},
    measureFilter: chartConfig.measureFilter,
    segments: (chartConfig as any).segments || [],
  });

  // Create series configuration - handle both regular y-axis fields and segmentation series
  const series = useMemo(() => {
    if (!chartData || chartData.dataset.length === 0) return [];

    // Get all keys from the dataset except 'x'
    const dataKeys = Object.keys(chartData.dataset[0]).filter(
      (key) => key !== "x"
    );

    return dataKeys.map((field, index) => ({
      dataKey: field,
      label: chartConfig.datasetLabels?.[field] || field,
      stack: "total", // This makes the bars stacked
    }));
  }, [chartData, chartConfig.datasetLabels]);

  if (!chartData) {
    return null;
  }

  const { dataset } = chartData;

  const renderChart = () => {
    switch (chartConfig.chartType) {
      case "bar":
        return (
          <BarChart
            dataset={dataset}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "x",
                valueFormatter: (value) => {
                  // Convert timestamp to readable date
                  if (typeof value === "number" && value > 1000000000000) {
                    // This is likely a Unix timestamp in milliseconds
                    return datejs.utc(value).format("MM/DD/YYYY");
                  }
                  return String(value);
                },
              },
            ]}
            yAxis={[
              {
                tickNumber: 5,
                tickMinStep: 50, // Ensure minimum spacing between ticks
                valueFormatter: (value: any) => {
                  // Format large numbers to be more readable
                  const numValue = Number(value);
                  if (numValue >= 1000000) {
                    return `${(numValue / 1000000).toFixed(1)}M`;
                  } else if (numValue >= 1000) {
                    return `${(numValue / 1000).toFixed(0)}K`; // Remove decimal for cleaner look
                  }
                  return numValue.toLocaleString();
                },
                // Add spacing and positioning options
                labelStyle: {
                  fontSize: "12px",
                  fontWeight: "500",
                },
              },
            ]}
            series={series}
            slotProps={{
              legend: {
                direction: "horizontal",
                position: { vertical: "bottom", horizontal: "center" },
              },
            }}
            sx={{
              width: "100%",
              height: "100%",
              // Add margin to provide more space for axis labels
              margin: { left: 80, right: 20, top: 20, bottom: 60 },
            }}
          />
        );
      case "line":
        return (
          <LineChart
            dataset={dataset}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "x",
                valueFormatter: (value) => {
                  // Convert timestamp to readable date
                  if (typeof value === "number" && value > 1000000000000) {
                    // This is likely a Unix timestamp in milliseconds
                    return datejs.utc(value).format("MM/DD/YYYY");
                  }
                  return String(value);
                },
              },
            ]}
            yAxis={[
              {
                tickNumber: 5,
                tickMinStep: 50, // Ensure minimum spacing between ticks
                valueFormatter: (value: any) => {
                  // Format large numbers to be more readable
                  const numValue = Number(value);
                  if (numValue >= 1000000) {
                    return `${(numValue / 1000000).toFixed(1)}M`;
                  } else if (numValue >= 1000) {
                    return `${(numValue / 1000).toFixed(0)}K`; // Remove decimal for cleaner look
                  }
                  return numValue.toLocaleString();
                },
                // Add spacing and positioning options
                labelStyle: {
                  fontSize: "12px",
                  fontWeight: "500",
                },
              },
            ]}
            series={series}
            slotProps={{
              legend: {
                direction: "horizontal",
                position: { vertical: "bottom", horizontal: "center" },
              },
            }}
            sx={{
              width: "100%",
              height: "100%",
              // Add margin to provide more space for axis labels
              margin: { left: 80, right: 20, top: 20, bottom: 60 },
            }}
          />
        );
      default:
        return (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Unsupported chart type: {chartConfig.chartType}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ flex: 1, height: "100%", width: "100%" }}>{renderChart()}</Box>
  );
};
